/**
 * Notifications Management Page
 *
 * Admin interface for creating, viewing, and managing user notifications.
 * Issue #241 - Admin notification system
 */

import { useEffect, useMemo, useState } from 'react';
import { HelpTooltip } from '../components/HelpTooltip';
import { UserSelector } from '../components/UserSelector';
import {
  apiClient,
  type CreateNotificationPayload,
  type MultiLangText,
  type Notification,
  type NotificationLevel,
  type NotificationPriority,
  type NotificationTargetType,
  type SupportedLanguage
} from '../lib/api-client';

interface Stats {
  totalCount: number;
  activeCount: number;
  expiredCount: number;
}

const SUPPORTED_LANGUAGES: Array<{ code: SupportedLanguage; name: string; flag: string }> = [
  { code: 'en', name: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'ja', name: '\u65E5\u672C\u8A9E', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'es', name: 'Espa\u00F1ol', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'fr', name: 'Fran\u00E7ais', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'ko', name: '\uD55C\uAD6D\uC5B4', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: 'de', name: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'pt', name: 'Portugu\u00EAs', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: 'it', name: 'Italiano', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'tr', name: 'T\u00FCrk\u00E7e', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: 'zh-cn', name: '\u7B80\u4F53\u4E2D\u6587', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: 'zh-tw', name: '\u7E41\u9AD4\u4E2D\u6587', flag: '\u{1F1F9}\u{1F1FC}' }
];

/**
 * Remove empty/whitespace-only entries from a multi-language text object.
 * Ensures only meaningful translations are saved.
 */
function cleanMultiLangText(obj: MultiLangText): MultiLangText {
  const cleaned: MultiLangText = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value && value.trim().length > 0) {
      cleaned[key as SupportedLanguage] = value;
    }
  }
  return cleaned;
}

/**
 * Pick the admin-facing preview text: English first, then any available language.
 */
function previewText(text: MultiLangText): string {
  return text.en ?? Object.values(text).find(value => value) ?? '';
}

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterLevel, setFilterLevel] = useState<string>('ALL');
  const [filterPriority, setFilterPriority] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL'); // ALL, ACTIVE, EXPIRED

  // Create form state
  const [activeTab, setActiveTab] = useState<SupportedLanguage>('en');
  const langNameMap = useMemo(
    () => new Map(SUPPORTED_LANGUAGES.map(({ code, name }) => [code, name])),
    []
  );
  const [formData, setFormData] = useState({
    title: {} as MultiLangText,
    message: {} as MultiLangText,
    level: 'INFO' as NotificationLevel,
    priority: 'MEDIUM' as NotificationPriority,
    targetType: 'ALL' as NotificationTargetType,
    targetUserIds: [] as string[],
    expiresAt: ''
  });

  useEffect(() => {
    void fetchNotifications();
    void fetchStats();
  }, []);

  const fetchNotifications = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiClient.getNotifications();
      if (Array.isArray(data.notifications)) {
        setNotifications(data.notifications);
      } else {
        setNotifications([]);
        setError('Invalid response from server');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setNotifications([]); // Ensure notifications is always an array
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiClient.getNotificationStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: {},
      message: {},
      level: 'INFO',
      priority: 'MEDIUM',
      targetType: 'ALL',
      targetUserIds: [],
      expiresAt: ''
    });
    setActiveTab('en');
  };

  const updateFormField = (field: 'title' | 'message', lang: SupportedLanguage, value: string) => {
    setFormData(prev => ({ ...prev, [field]: { ...prev[field], [lang]: value } }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const cleanedTitle = cleanMultiLangText(formData.title);
      const cleanedMessage = cleanMultiLangText(formData.message);

      // 英語はフォールバック言語として API 側でも必須
      if (!cleanedTitle.en || !cleanedMessage.en) {
        setError('English (en) title and message are required as fallback');
        return;
      }

      const payload: CreateNotificationPayload = {
        title: cleanedTitle,
        message: cleanedMessage,
        level: formData.level,
        priority: formData.priority,
        targetType: formData.targetType
      };

      if (formData.targetType === 'SPECIFIC' && formData.targetUserIds.length > 0) {
        payload.targetUserIds = formData.targetUserIds;
      }

      if (formData.expiresAt) {
        payload.expiresAt = new Date(formData.expiresAt).toISOString();
      }

      await apiClient.createNotification(payload);

      // Reset form and close modal
      resetForm();
      setShowCreateModal(false);

      // Refresh lists
      void fetchNotifications();
      void fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create notification');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await apiClient.deleteNotification(id);

      void fetchNotifications();
      void fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete notification');
    }
  };

  const getLevelBadge = (level: string) => {
    const colors = {
      INFO: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      WARNING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      ERROR: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      SUCCESS: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    };
    return colors[level as keyof typeof colors] || colors.INFO;
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      LOW: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      MEDIUM: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      HIGH: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      URGENT: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return colors[priority as keyof typeof colors] || colors.MEDIUM;
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filterLevel !== 'ALL' && notification.level !== filterLevel) return false;
    if (filterPriority !== 'ALL' && notification.priority !== filterPriority) return false;
    // Use server-provided expiration status instead of client-side calculation
    if (filterStatus === 'ACTIVE' && notification.isExpired) return false;
    if (filterStatus === 'EXPIRED' && !notification.isExpired) return false;
    return true;
  });

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Notifications</h1>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Manage user notifications and announcements
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700'
        >
          Create Notification
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className='grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-4'>
          <div className='rounded-lg bg-white p-3 shadow md:p-6 dark:bg-gray-800'>
            <p className='text-xs font-medium text-gray-600 md:text-sm dark:text-gray-400'>Total</p>
            <p className='mt-1 text-xl font-bold text-gray-900 md:mt-2 md:text-3xl dark:text-white'>
              {stats.totalCount}
            </p>
          </div>
          <div className='rounded-lg bg-white p-3 shadow md:p-6 dark:bg-gray-800'>
            <p className='text-xs font-medium text-gray-600 md:text-sm dark:text-gray-400'>
              Active
            </p>
            <p className='mt-1 text-xl font-bold text-green-600 md:mt-2 md:text-3xl dark:text-green-400'>
              {stats.activeCount}
            </p>
          </div>
          <div className='rounded-lg bg-white p-3 shadow md:p-6 dark:bg-gray-800'>
            <p className='text-xs font-medium text-gray-600 md:text-sm dark:text-gray-400'>
              Expired
            </p>
            <p className='mt-1 text-xl font-bold text-gray-600 md:mt-2 md:text-3xl dark:text-gray-400'>
              {stats.expiredCount}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className='flex flex-wrap gap-4 rounded-lg bg-white p-4 shadow dark:bg-gray-800'>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Level
          </label>
          <select
            value={filterLevel}
            onChange={e => setFilterLevel(e.target.value)}
            className='rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white'
          >
            <option value='ALL'>All Levels</option>
            <option value='INFO'>Info</option>
            <option value='WARNING'>Warning</option>
            <option value='ERROR'>Error</option>
            <option value='SUCCESS'>Success</option>
          </select>
        </div>

        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Priority
          </label>
          <select
            value={filterPriority}
            onChange={e => setFilterPriority(e.target.value)}
            className='rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white'
          >
            <option value='ALL'>All Priorities</option>
            <option value='LOW'>Low</option>
            <option value='MEDIUM'>Medium</option>
            <option value='HIGH'>High</option>
            <option value='URGENT'>Urgent</option>
          </select>
        </div>

        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Status
          </label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className='rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white'
          >
            <option value='ALL'>All Status</option>
            <option value='ACTIVE'>Active</option>
            <option value='EXPIRED'>Expired</option>
          </select>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className='rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200'>
          {error}
        </div>
      )}

      {/* Notifications List */}
      <div className='rounded-lg bg-white shadow dark:bg-gray-800'>
        {isLoading ? (
          <div className='flex items-center justify-center p-12'>
            <div className='h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent'></div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className='p-12 text-center text-gray-500 dark:text-gray-400'>
            No notifications found
          </div>
        ) : (
          <div className='divide-y divide-gray-200 dark:divide-gray-700'>
            {filteredNotifications.map(notification => (
              <div key={notification.id} className='p-4 hover:bg-gray-50 dark:hover:bg-gray-700'>
                <div className='flex items-start justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2'>
                      <h3 className='font-semibold text-gray-900 dark:text-white'>
                        {previewText(notification.title)}
                      </h3>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${getLevelBadge(notification.level)}`}
                      >
                        {notification.level}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${getPriorityBadge(notification.priority)}`}
                      >
                        {notification.priority}
                      </span>
                      {notification.isExpired && (
                        <span className='rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300'>
                          EXPIRED
                        </span>
                      )}
                    </div>
                    <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
                      {previewText(notification.message)}
                    </p>
                    <div className='mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500'>
                      <span>Target: {notification.target_type}</span>
                      <span>Created: {new Date(notification.created_at).toLocaleString()}</span>
                      {notification.expires_at && (
                        <span>Expires: {new Date(notification.expires_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(notification.id)}
                    className='ml-4 rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className='bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4'>
          <div className='max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800'>
            <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
              Create Notification
            </h2>
            <form onSubmit={handleCreate} className='mt-6 space-y-4'>
              {/* Language Tabs */}
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Languages
                  <HelpTooltip content='Select a language tab to enter content for that language. English (en) is required as fallback.' />
                </label>
                <div className='flex flex-wrap gap-2 border-b border-gray-200 pb-2 dark:border-gray-700'>
                  {SUPPORTED_LANGUAGES.map(({ code, name, flag }) => (
                    <button
                      key={code}
                      type='button'
                      onClick={() => setActiveTab(code)}
                      className={`rounded-t-lg px-3 py-2 text-sm font-medium transition-colors ${
                        activeTab === code
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      } ${code === 'en' ? 'ring-2 ring-yellow-400' : ''}`}
                    >
                      {flag} {name}
                      {code === 'en' && ' *'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Title ({activeTab}) {activeTab === 'en' && '*'}
                </label>
                <input
                  type='text'
                  required={activeTab === 'en'}
                  maxLength={200}
                  value={formData.title[activeTab] || ''}
                  onChange={e => updateFormField('title', activeTab, e.target.value)}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  placeholder={`Enter title in ${langNameMap.get(activeTab)}`}
                />
              </div>

              <div>
                <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Message ({activeTab}) {activeTab === 'en' && '*'}
                </label>
                <textarea
                  required={activeTab === 'en'}
                  maxLength={1000}
                  rows={4}
                  value={formData.message[activeTab] || ''}
                  onChange={e => updateFormField('message', activeTab, e.target.value)}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  placeholder={`Enter message in ${langNameMap.get(activeTab)}`}
                />
              </div>

              <div className='grid gap-4 md:grid-cols-2'>
                <div>
                  <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Level
                  </label>
                  <select
                    value={formData.level}
                    onChange={e =>
                      setFormData({ ...formData, level: e.target.value as Notification['level'] })
                    }
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  >
                    <option value='INFO'>Info</option>
                    <option value='WARNING'>Warning</option>
                    <option value='ERROR'>Error</option>
                    <option value='SUCCESS'>Success</option>
                  </select>
                </div>

                <div>
                  <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as Notification['priority']
                      })
                    }
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  >
                    <option value='LOW'>Low</option>
                    <option value='MEDIUM'>Medium</option>
                    <option value='HIGH'>High</option>
                    <option value='URGENT'>Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Target Type
                </label>
                <select
                  value={formData.targetType}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      targetType: e.target.value as Notification['target_type']
                    })
                  }
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                >
                  <option value='ALL'>All Users</option>
                  <option value='SPECIFIC'>Specific Users</option>
                </select>
              </div>

              {formData.targetType === 'SPECIFIC' && (
                <div>
                  <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Select Users *
                  </label>
                  <UserSelector
                    selectedUserIds={formData.targetUserIds}
                    onChange={userIds => setFormData({ ...formData, targetUserIds: userIds })}
                    placeholder='Search users by handle or display name...'
                  />
                </div>
              )}

              <div>
                <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Expires At (optional)
                </label>
                <input
                  type='datetime-local'
                  value={formData.expiresAt}
                  onChange={e => setFormData({ ...formData, expiresAt: e.target.value })}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                />
              </div>

              <div className='flex justify-end gap-2 pt-4'>
                <button
                  type='button'
                  onClick={() => setShowCreateModal(false)}
                  className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700'
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
